import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Database, Lock, RotateCw } from 'lucide-react';
import type {
  PrototypeApiOperationDto,
  PrototypeApiSchemaDto,
  PrototypeApiSpecUnavailableReason,
} from '@aiai/shared';
import { fetchPrototypeApiSpec, fetchPrototypeSchema } from '../api';
import { qk } from '../api/queryKeys';

const METHOD_CLASS: Record<string, string> = {
  GET: 'bg-info-soft text-info',
  POST: 'bg-success-soft text-success',
  PUT: 'bg-warning-soft text-warning',
  PATCH: 'bg-warning-soft text-warning',
  DELETE: 'bg-danger-soft text-danger',
};

const UNAVAILABLE_TEXT: Record<PrototypeApiSpecUnavailableReason, string> = {
  static: '', // never shown: the card is hidden for static prototypes
  missing:
    'No API description yet. Prototypes created before API docs went private need a small server change first — ask the Engineer to "make the API docs private". Newer prototypes: redeploy to see API docs.',
  oversize: 'The API description is too large to show. Redeploy to see API docs.',
  malformed: "The API description couldn't be read. Redeploy to see API docs.",
};

/** Schema names inside a label like `Item[]`, `A | B`, `A & B`. */
function schemaNames(label: string): string[] {
  return label
    .split(/\s*[|&]\s*/)
    .map((p) => p.replace(/(\[\])+$/, ''))
    .filter(Boolean);
}

/** `CreateItemDto` / `ItemResponse` / `Item` → `item` — to match an ERD model. */
function baseName(name: string): string {
  return name
    .replace(/^(Create|Update|Patch)(?=[A-Z])/, '')
    .replace(/(Dto|Response|Entity|Model)$/, '')
    .toLowerCase();
}

/**
 * Renders a schema label; names that match a table in the database diagram
 * link to it.
 */
function SchemaLabel({ label, erdModels }: { label: string; erdModels: Set<string> }) {
  const linked = schemaNames(label).some((n) => erdModels.has(baseName(n)));
  if (!linked) return <code className="font-mono text-ink">{label}</code>;
  return (
    <a
      href="#prototype-erd"
      className="inline-flex items-center gap-1 font-mono text-info hover:underline"
      title="Matches a table in the database diagram"
    >
      {label}
      <Database className="h-3 w-3" aria-label="database table" />
    </a>
  );
}

function SchemaFields({ name, schema }: { name: string; schema: PrototypeApiSchemaDto }) {
  return (
    <div className="rounded-lg border border-line bg-surface-0 p-2">
      <div className="mb-1 font-mono text-xs font-semibold text-ink">{name}</div>
      {schema.properties.length === 0 ? (
        <div className="text-xs text-ink-faint">{schema.type ?? 'no properties'}</div>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {schema.properties.map((p) => (
            <li key={p.name} className="font-mono text-ink-muted">
              {p.name}
              {p.required ? '' : '?'}: <span className="text-ink">{p.type ?? 'any'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OperationRow({
  op,
  schemas,
  erdModels,
}: {
  op: PrototypeApiOperationDto;
  schemas: Record<string, PrototypeApiSchemaDto>;
  erdModels: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const labels = [op.requestBody, ...op.responses.map((r) => r.schema)].filter(
    (l): l is string => !!l,
  );
  const referenced = [...new Set(labels.flatMap(schemaNames))].filter((n) => schemas[n]);
  return (
    <li className="border-t border-line first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-2"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-ink-faint transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <span
          className={`w-16 shrink-0 rounded px-1.5 py-0.5 text-center font-mono text-[11px] font-semibold ${
            METHOD_CLASS[op.method] ?? 'bg-surface-2 text-ink-muted'
          }`}
        >
          {op.method}
        </span>
        <span className="min-w-0 truncate font-mono text-xs text-ink">{op.path}</span>
        {op.requiresAuth && (
          <Lock className="h-3.5 w-3.5 shrink-0 text-warning" aria-label="Requires authentication" />
        )}
        {op.summary && (
          <span className="ml-auto hidden truncate text-xs text-ink-faint sm:block">{op.summary}</span>
        )}
      </button>
      {open && (
        <div className="space-y-3 px-3 pb-3 pl-9 text-xs text-ink-muted">
          {op.summary && <p className="sm:hidden">{op.summary}</p>}
          {op.requiresAuth && <p>Requires a signed-in user (bearer token).</p>}
          <div>
            <div className="mb-1 font-semibold text-ink">Parameters</div>
            {op.params.length === 0 ? (
              <div className="text-ink-faint">None</div>
            ) : (
              <ul className="space-y-0.5">
                {op.params.map((p) => (
                  <li key={`${p.in}:${p.name}`} className="font-mono">
                    {p.name}
                    {p.required ? '' : '?'}{' '}
                    <span className="text-ink-faint">({p.in})</span>
                    {p.type && <span className="text-ink">: {p.type}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {op.requestBody && (
            <div>
              <div className="mb-1 font-semibold text-ink">Request body</div>
              <SchemaLabel label={op.requestBody} erdModels={erdModels} />
            </div>
          )}
          <div>
            <div className="mb-1 font-semibold text-ink">Responses</div>
            {op.responses.length === 0 ? (
              <div className="text-ink-faint">Not documented</div>
            ) : (
              <ul className="space-y-0.5">
                {op.responses.map((r) => (
                  <li key={r.status}>
                    <span className="font-mono text-ink">{r.status}</span>
                    {r.description && <span> — {r.description}</span>}
                    {r.schema && (
                      <>
                        {' '}
                        <SchemaLabel label={r.schema} erdModels={erdModels} />
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {referenced.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {referenced.map((n) => (
                <SchemaFields key={n} name={n} schema={schemas[n]} />
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Read-only view of a full-stack prototype's API, rendered from the OpenAPI
 * document its deployed container writes to its private data dir (the public
 * URL no longer serves `/api/docs`). No request console — browse only.
 * Renders nothing for static prototypes.
 */
export function PrototypeApiCard({ prototypeId }: { prototypeId: string }) {
  const specQuery = useQuery({
    queryKey: qk.prototypeApiSpec(prototypeId),
    queryFn: () => fetchPrototypeApiSpec(prototypeId),
  });
  // Shares the DB diagram's cache entry — used to link schemas to its tables.
  const schemaQuery = useQuery({
    queryKey: qk.prototypeSchema(prototypeId),
    queryFn: () => fetchPrototypeSchema(prototypeId),
  });

  if (specQuery.isLoading) {
    return (
      <div>
        <h2 className="mb-3 text-sm font-semibold text-ink">API</h2>
        <div
          role="status"
          aria-live="polite"
          className="h-40 animate-pulse rounded-xl border border-line bg-surface-2"
        />
      </div>
    );
  }

  const spec = specQuery.data;
  // Additive, like the DB diagram: hide on a fetch error or a static prototype.
  if (!spec || (!spec.available && spec.reason === 'static')) return null;

  const refresh = (
    <button
      type="button"
      className="btn-secondary btn-sm"
      onClick={() => specQuery.refetch()}
      disabled={specQuery.isFetching}
      title="Re-read the API description from the deployed prototype"
    >
      <RotateCw
        className={`h-3 w-3 ${specQuery.isFetching ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      Refresh
    </button>
  );

  if (!spec.available) {
    return (
      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">API</h2>
          {refresh}
        </div>
        <p className="rounded-xl border border-line bg-surface-0 p-4 text-sm text-ink-muted">
          {UNAVAILABLE_TEXT[spec.reason]}
        </p>
      </div>
    );
  }

  const erdModels = new Set(
    (schemaQuery.data?.available ? schemaQuery.data.models : []).map((m) => m.name.toLowerCase()),
  );
  const count = spec.tags.reduce((n, t) => n + t.operations.length, 0);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">API</h2>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-1.5 text-xs text-ink-muted sm:inline-flex">
            <Lock className="h-3.5 w-3.5 text-warning" aria-hidden="true" /> requires auth
          </span>
          <span className="text-xs text-ink-faint">
            {count} {count === 1 ? 'endpoint' : 'endpoints'}
          </span>
          {refresh}
        </div>
      </div>
      {count === 0 ? (
        <p className="rounded-xl border border-line bg-surface-0 p-4 text-sm text-ink-muted">
          The prototype's API has no documented endpoints yet.
        </p>
      ) : (
        <div className="space-y-3">
          {spec.tags.map((tag) => (
            <section
              key={tag.name}
              aria-label={`${tag.name} endpoints`}
              className="overflow-hidden rounded-xl border border-line bg-surface-0"
            >
              <h3 className="border-b border-line bg-surface-1 px-3 py-1.5 text-xs font-semibold text-ink">
                {tag.name}
              </h3>
              <ul>
                {tag.operations.map((op) => (
                  <OperationRow
                    key={`${op.method} ${op.path}`}
                    op={op}
                    schemas={spec.schemas}
                    erdModels={erdModels}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      <p className="mt-2 text-xs text-ink-faint">
        Read-only view of the deployed prototype's API. It is not published on the prototype's
        public URL.
      </p>
    </div>
  );
}
